import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useSpring, useInView } from 'framer-motion';
import {
  ArrowRight,
  Cpu,
  ShieldCheck,
  Menu,
  X,
  Zap,
  ChevronRight,
  Activity,
  TrendingUp,
  Lock,
  BarChart3,
  Briefcase,
  Bot,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Youtube,
  Instagram,
  Send,
  MessageCircle,
  MousePointer2,
  Check,
  Database,
  Code2,
  Layers,
  Settings,
  Workflow
} from 'lucide-react';

// --- Animation Variants ---

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

const menuVars = {
  initial: { scaleY: 0 },
  animate: { 
    scaleY: 1, 
    transition: { duration: 0.5, ease: [0.12, 0, 0.39, 0] }
  },
  exit: { 
    scaleY: 0, 
    transition: { delay: 0.2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

const navLinkVars = {
  initial: { y: "30vh", transition: { duration: 0.5, ease: [0.37, 0, 0.63, 1] } },
  open: { y: 0, transition: { ease: [0, 0.55, 0.45, 1], duration: 0.7 } }
};

// --- Components ---

const BackToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ 
        opacity: isVisible ? 1 : 0, 
        scale: isVisible ? 1 : 0.5,
        y: isVisible ? 0 : 20
      }}
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 z-50 group"
    >
      <div className="relative flex items-center justify-center p-4 bg-white rounded-full shadow-2xl border border-neutral-100 hover:bg-black transition-colors duration-500">
        <svg className="absolute inset-0 w-full h-full -rotate-90">
          <motion.circle
            cx="50%"
            cy="50%"
            r="44%"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-neutral-200"
          />
          <motion.circle
            cx="50%"
            cy="50%"
            r="44%"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ pathLength: scrollYProgress }}
            className="text-black group-hover:text-white"
          />
        </svg>
        <ArrowRight className="-rotate-90 text-black group-hover:text-white transition-colors duration-500" size={20} />
      </div>
    </motion.button>
  );
};

const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <motion.div
      className="fixed top-0 left-0 right-0 h-1 bg-black origin-left z-[60]"
      style={{ scaleX }}
    />
  );
};

const Navbar = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      
      // ScrollSpy logic
      if (location.pathname === '/') {
        const sections = ['services', 'products', 'careers', 'company', 'contact'];
        const current = sections.find(section => {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            return rect.top <= 100 && rect.bottom >= 100;
          }
          return false;
        });
        setActiveSection(current || (window.scrollY < 200 ? 'home' : ''));
      } else {
        setActiveSection('');
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', href: '/', id: 'home' },
    { name: 'Services', href: '/services', id: 'services' },
    { name: 'Products', href: '/products', id: 'products' },
    { name: 'Careers', href: '/careers', id: 'careers' },
    { name: 'Company', href: '/company', id: 'company' },
    { name: 'Contact', href: '/contact', id: 'contact' },
  ];

  const handleNavClick = (e, item) => {
    // We remove the preventDefault to allow standard redirection to the dedicated pages
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav 
        className={`fixed w-full z-50 top-0 transition-all duration-300 ${
          scrolled ? 'py-2 bg-white/90 backdrop-blur-md border-b border-black/5' : 'py-3 bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo Section */}
          <a href="/" className="flex items-center group relative z-50">
            <img src="/static/arham_symbol.webp" alt="ARHAM Symbol" className="h-12 w-auto object-contain" />
          </a>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center gap-4 lg:gap-10">
            {navLinks.map((item) => (
              <a 
                key={item.name} 
                href={item.href}
                onClick={(e) => handleNavClick(e, item)}
                className={`text-xs font-medium uppercase tracking-widest transition-all duration-300 relative group ${
                  activeSection === item.id ? 'text-black' : 'text-neutral-500 hover:text-black'
                }`}
              >
                {item.name}
                <span className={`absolute -bottom-1 left-0 h-px bg-black transition-all duration-300 ${
                  activeSection === item.id ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </a>
            ))}
          </div>

          {/* CTA & Mobile Toggle */}
            <div className="flex items-center z-50">
            <button 
              className="md:hidden text-black p-2" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </nav>



      {/* Mobile Full Screen Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            variants={menuVars}
            initial="initial"
            animate="animate"
            exit="exit"
            className="fixed inset-0 bg-white z-40 origin-top flex flex-col justify-center items-center"
          >
            <div className="flex flex-col gap-8 text-center">
              {navLinks.map((link, index) => (
                <motion.div
                  key={index}
                  variants={navLinkVars}
                  initial="initial"
                  animate="open"
                  custom={index}
                >
                    <a 
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-4xl font-light text-black tracking-tight hover:italic transition-all"
                  >
                    {link.name}
                  </a>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Hero = () => {
  return (
    <section className="relative min-h-[600px] flex items-center pt-12 pb-12 overflow-hidden bg-white">
      {/* Background Gradients */}
      <div className="absolute top-0 right-0 w-[50vw] h-[50vw] bg-neutral-100 rounded-full blur-3xl opacity-60 -translate-y-1/4 translate-x-1/4 pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 w-full grid lg:grid-cols-2 gap-12 lg:gap-24 items-center relative z-10">
        
        {/* Left: Content */}
        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="space-y-8 text-center lg:text-left"
        >

          <motion.div variants={fadeInUp}>
            <img
              src="/static/arham_text.webp"
              alt="ARHAM FinTech"
              className="h-28 md:h-36 lg:h-44 w-auto object-contain mx-auto lg:mx-0"
            />
          </motion.div>

          <motion.p variants={fadeInUp} className="text-lg lg:text-xl text-neutral-600 leading-relaxed max-w-lg mx-auto lg:mx-0">
            We build software for people who live in the markets. Whether you're tracking dozens of tickers, running a strategy, managing a team, or just trying to automate that one annoying task — we'll help you make it real.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <a href="/careers" className="bg-black text-white px-8 py-3 rounded-full font-medium hover:bg-neutral-800 transition-colors">Join Our Team</a>
            <a href="/products" className="border border-black text-black px-8 py-3 rounded-full font-medium hover:bg-neutral-100 transition-colors">View Products</a>
          </motion.div>

          <motion.div variants={fadeInUp} className="pt-8 flex items-center justify-center lg:justify-start gap-8 opacity-60 grayscale">
             {/* Simple Logos or Trust Markers */}
             {['NSE', 'BSE', 'MCX'].map(ex => (
                <span key={ex} className="font-bold text-xl text-neutral-300 tracking-widest">{ex}</span>
             ))}
          </motion.div>
        </motion.div>

        {/* Right: 3D Visual */}
        <motion.div
           initial={{ opacity: 0, x: 100 }}
           animate={{ opacity: 1, x: 0 }}
           transition={{ duration: 1.2, ease: "easeOut" }}
           className="relative h-[500px] w-full hidden lg:block perspective-[1200px]"
        >
           {/* The Floating Card Container */}
           <motion.div
             animate={{ rotateY: [-5, 5], rotateX: [2, -2] }}
             transition={{ duration: 6, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
             className="w-full h-full transform-style-3d"
           >
              {/* Main Dashboard Panel */}
              <div className="absolute inset-0 bg-white border border-neutral-200 rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                 <div className="h-10 bg-neutral-50 border-b border-neutral-100 flex items-center px-4 gap-2">
                    <div className="flex gap-1.5">
                       <div className="w-2.5 h-2.5 rounded-full bg-neutral-300" />
                       <div className="w-2.5 h-2.5 rounded-full bg-neutral-300" />
                    </div>
                    <div className="ml-auto text-[10px] font-mono text-neutral-400">STATUS: CONNECTED</div>
                 </div>
                 
                 <div className="flex-1 p-6 flex flex-col gap-6">
                    {/* Graph Area */}
                    <div className="flex-1 bg-neutral-50 rounded-lg border border-neutral-100 relative overflow-hidden group">
                        <div className="absolute inset-0 flex items-end justify-between px-4 pb-0 pt-8 gap-2 opacity-50">
                           {[30, 50, 45, 70, 60, 80, 55, 90, 65, 85].map((h, i) => (
                              <motion.div
                                key={i}
                                initial={{ height: 0 }}
                                animate={{ height: `${h}%` }}
                                transition={{ duration: 1, delay: 0.5 + (i * 0.05) }}
                                className="w-full bg-black rounded-t-sm opacity-80 group-hover:bg-neutral-800 transition-colors"
                              />
                           ))}
                        </div>
                        {/* Overlay Line */}
                        <svg className="absolute inset-0 w-full h-full overflow-visible pointer-events-none">
                           <motion.path
                              d="M0,150 C50,150 50,100 100,100 C150,100 150,50 200,50 C250,50 250,120 300,120 C350,120 350,20 400,20"
                              fill="none"
                              stroke="black"
                              strokeWidth="2"
                              initial={{ pathLength: 0 }}
                              animate={{ pathLength: 1 }}
                              transition={{ duration: 2, ease: "easeInOut" }}
                           />
                        </svg>
                    </div>

                    {/* Stats Row */}
                    <div className="h-24 grid grid-cols-2 gap-4">
                        <div className="bg-black text-white p-4 rounded-lg flex flex-col justify-between">
                            <Zap size={16} />
                            <div>
                               <div className="text-2xl font-bold">24ms</div>
                               <div className="text-[10px] text-neutral-400 uppercase">Latency</div>
                            </div>
                        </div>
                        <div className="border border-neutral-200 p-4 rounded-lg flex flex-col justify-between">
                            <Activity size={16} className="text-neutral-400" />
                             <div>
                               <div className="text-2xl font-bold text-black">99.9%</div>
                               <div className="text-[10px] text-neutral-400 uppercase">Uptime</div>
                            </div>
                        </div>
                    </div>
                 </div>
              </div>

              {/* Floating Element Behind */}
              <motion.div 
                 className="absolute -z-10 -right-8 -bottom-8 w-48 h-48 bg-neutral-900 rounded-2xl"
                 animate={{ y: [0, 20, 0] }}
                 transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
           </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const Marquee = () => (
    <div className="bg-white py-16">
        <div className="bg-black py-4 overflow-hidden border-y border-neutral-800">
            <motion.div 
                className="flex whitespace-nowrap"
                animate={{ x: ["0%", "-50%"] }}
                transition={{ duration: 20, ease: "linear", repeat: Infinity }}
            >
                {[...Array(2)].map((_, i) => (
                    <div key={i} className="flex gap-16 mx-8 items-center">
                        {["HIGH FREQUENCY", "ALGORITHMIC TRADING", "RISK MANAGEMENT", "SECURE API", "LOW LATENCY", "REAL-TIME DATA"].map((text) => (
                            <div key={text} className="flex items-center gap-4">
                                <span className="text-white text-sm font-bold tracking-widest">{text}</span>
                                <div className="w-1.5 h-1.5 bg-neutral-700 rounded-full" />
                            </div>
                        ))}
                    </div>
                ))}
            </motion.div>
        </div>
    </div>
);

const Products = () => {
    const products = [
        { 
            id: "ipo-utility",
            title: "IPO Utility", 
            tag: "Utility",
            icon: <TrendingUp size={24} />, 
          desc: "Smart IPO Premium Management for Brokers",
        },
        { 
            id: "bounceback-trading",
            title: "Bounceback Trading Suite", 
            tag: "Trading",
            icon: <Activity size={24} />, 
            desc: "When Circuits Crack, You'll Be the First to Know. Let Bounceback Trading Suite do the watching for you. This smart tool keeps an eye on stocks in lower circuit and tells you when buyers are almost done absorbing the sell wall." 
        },
        { 
            id: "pair-trading",
            title: "Pair Trading", 
            tag: "Strategy",
            icon: <Zap size={24} />, 
            desc: "Enhance your edge. Execute with discipline. Grow consistently. By blending data precision with automation, the Pair Trading Suite puts a professional-grade strategy into your hands—without the need for complex coding or constant monitoring." 
        },
        { 
            id: "corporate-actions",
            title: "Corporate Actions", 
            tag: "Tracking",
            icon: <Briefcase size={24} />, 
            desc: "Track corporate actions like dividends, bonuses, mergers & more — categorized, searchable, and AI-summarized for speed." 
        },
        { 
            id: "ofs-cut-off",
            title: "OFS Cut-Off Price Predictor", 
            tag: "Prediction",
            icon: <BarChart3 size={24} />, 
            desc: "See real-time demand, calculate cut-off ranges, and place automated bids — designed for active investors and professionals." 
        },
        { 
            id: "ipo-allotment",
            title: "IPO Allotment Checker", 
            tag: "Utility",
            icon: <Bot size={24} />, 
            desc: "Upload PANs in Excel, check allotments across major registrars, and get your results in the same file — no more manual work." 
        }
    ];

    return (
        <section id="products" className="pb-32 bg-white relative overflow-hidden">
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">
                            Explore Our Products
                        </h2>
                        <p className="text-neutral-500 text-lg max-w-xl">
                            Check Out Our Different Range of products.
                        </p>
                    </motion.div>
                    
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {products.map((item, index) => (
                        <motion.a
                            key={index}
                            href={`/product/${item.id}`}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                            whileHover={{ y: -10 }}
                            className="group relative bg-white border border-neutral-200 p-8 rounded-3xl h-full flex flex-col justify-between hover:shadow-2xl hover:shadow-black/5 hover:border-black/20 transition-all duration-300 cursor-pointer"
                        >
                            <div className="mb-8">
                                <div className="flex justify-between items-start mb-6">
                                    <div className="w-14 h-14 bg-neutral-50 rounded-2xl flex items-center justify-center text-black group-hover:bg-black group-hover:text-white transition-colors duration-300">
                                        {item.icon}
                                    </div>
                                    <span className="px-3 py-1 bg-neutral-100 text-[10px] font-bold uppercase tracking-widest rounded-full text-neutral-500">
                                        {item.tag}
                                    </span>
                                </div>
                                <h3 className="text-2xl font-bold mb-3 text-black">{item.title}</h3>
                                <p className="text-neutral-500 leading-relaxed text-sm">
                                    {item.desc}
                                </p>
                            </div>

                            <div className="flex items-center text-sm font-bold group-hover:text-black text-neutral-400 transition-colors">
                                LEARN MORE <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                        </motion.a>
                    ))}
                </div>
            </div>
        </section>
    );
};

const ProductDetail = ({ productId }) => {
    const allProducts = {
        "ipo-utility": {
            title: "IPO Utility",
            tag: "Utility",
            icon: <TrendingUp size={40} />,
          desc: "Smart IPO Premium Management for Brokers",
          subtitle: "Smart IPO Premium Management for Brokers",
          fullDesc: `IPO Utility is a robust, browser-based application designed to simplify and streamline how brokers and investment teams handle IPO premium transactions. Built for high-volume use and real-time insights, it helps you take control of group-wise IPO applications, orders, and profitability—all in one place.

    Everything you need
    Whether you're handling one IPO or managing multiple deals across client groups, IPO Utility brings structure, automation, and insight to your workflow.`,
          features: [
            "IPO Setup & Customization — create and configure IPO deals with upper band price, issue size, and category-wise allocation; edit or delete IPOs anytime.",
            "Client & Group Management — add, modify, and categorize clients into custom groups; manage retail and HNI applications by group.",
            "Buy & Sell Order Management — record buy/sell orders with unit counts and premium values; group-based order placement and quick edits.",
            "Smart Data Entry & Integration — upload PAN-based Excel files, support TC/CS auto-parsing, and auto-map PAN, client name, quantity, and allotment info.",
            "Billing & Group-Level Insights — group-wise profit/loss summaries, total applications, allotted units, premium sales and billing breakdowns.",
            "Why Choose IPO Utility — eliminate manual Excel juggling, centralize IPO activity, and get clear profit/loss reporting with real-time edits and uploads."
          ],
          pricing: "Professional - ₹1,999/month",
          youtubeUrls: [
            "https://www.youtube.com/watch?v=4-FahbQEh0w",
            "https://www.youtube.com/watch?v=JvhF24_0r4E"
          ]
        },
        "bounceback-trading": {
            title: "Bounceback Trading Suite",
            tag: "Trading",
            icon: <Activity size={40} />,
            desc: "When Circuits Crack, You'll Be the First to Know. Let Bounceback Trading Suite do the watching for you. This smart tool keeps an eye on stocks in lower circuit and tells you when buyers are almost done absorbing the sell wall.",
            fullDesc: "Bounceback Trading Suite monitors lower circuit stocks in real-time, analyzing buying pressure and sell wall absorption. Get notified the moment momentum shifts, giving you the edge to enter at optimal prices before the crowd.",
            features: [
                "Lower circuit stock monitoring",
                "Sell wall absorption tracking",
                "Buyer momentum analysis",
                "Real-time circuit alerts",
                "Historical circuit data",
                "Smart entry point notifications"
            ],
            pricing: "Professional - ₹2,499/month",
            youtubeUrl: "https://www.youtube.com/watch?v=tN-uMnBEPOo"
        },
        "pair-trading": {
            title: "Pair Trading",
            tag: "Strategy",
            icon: <Zap size={40} />,
            desc: "Enhance your edge. Execute with discipline. Grow consistently. By blending data precision with automation, the Pair Trading Suite puts a professional-grade strategy into your hands—without the need for complex coding or constant monitoring.",
            fullDesc: "Pair Trading Suite automates the entire pair trading workflow—from identifying correlated pairs to executing trades with precision. Built for traders who want institutional-grade strategies without the complexity.",
            features: [
                "Automated pair identification",
                "Real-time correlation tracking",
                "Smart entry/exit signals",
                "Risk-adjusted position sizing",
                "Backtesting capabilities",
                "Performance analytics"
            ],
            pricing: "Professional - ₹2,999/month",
            youtubeUrl: "https://www.youtube.com/watch?v=iN-sy96S2dw&t=51s"
        },
        "corporate-actions": {
            title: "Corporate Actions",
            tag: "Tracking",
            icon: <Briefcase size={40} />,
            desc: "Track corporate actions like dividends, bonuses, mergers & more — categorized, searchable, and AI-summarized for speed.",
            fullDesc: "Never miss a corporate action again. Get real-time updates on dividends, stock splits, bonus issues, rights offerings, and mergers with AI-powered summaries.",
            features: [
                "Real-time action alerts",
                "AI-powered summaries",
                "Historical data access",
                "Dividend tracking",
                "Merger & acquisition updates",
                "Email notifications"
            ],
            pricing: "Standard - ₹999/month",
            playStore: "https://play.google.com/store/apps/details?id=com.agc.CorporateActions",
            youtubeUrl: "https://www.youtube.com/embed/bdM_iT2Ue4Y?si=weW9T7mtBpC53s30"
        },
        "ofs-cut-off": {
            title: "OFS Cut-Off Price Predictor",
            tag: "Prediction",
            icon: <BarChart3 size={40} />,
            desc: "See real-time demand, calculate cut-off ranges, and place automated bids — designed for active investors and professionals.",
            fullDesc: "Gain a competitive edge in OFS bidding. Our predictive models calculate cut-off ranges in real-time, helping you bid smarter and faster.",
            features: [
                "Real-time demand visualization",
                "Cut-off range predictions",
                "Historical bid analysis",
                "Automated bidding",
                "Success rate analytics",
                "Notification system"
            ],
            pricing: "Professional - ₹1,999/month",
            youtubeUrl: "https://www.youtube.com/watch?v=BofRb33B9kw"
        },
        "ipo-allotment": {
            title: "IPO Allotment Checker",
            tag: "Utility",
            icon: <Bot size={40} />,
            desc: "Upload PANs in Excel, check allotments across major registrars, and get your results in the same file — no more manual work.",
            fullDesc: "Eliminate the hassle of manual IPO allotment checking. Bulk upload your PANs and get instant results across all major registrars.",
            features: [
                "Bulk PAN upload",
                "Multi-registrar support",
                "One-click export",
                "Real-time updates",
                "Allotment notifications",
                "Historical records"
            ],
            pricing: "Standard - ₹499/month",
            youtubeUrl: "https://www.youtube.com/watch?v=XdE4xhVe1RY"
        }
    };

    const product = allProducts[productId];

    // Convert common YouTube watch/share/embed URLs to a sanitized embed URL suitable for iframe.
    // Adds `enablejsapi=1` and `origin` where possible and strips unknown query params (example: `si`) that can cause player configuration errors.
    const toEmbedUrl = (url) => {
      if (!url) return url;
      try {
        const u = new URL(url);
        const hostname = u.hostname.replace('www.', '');

        const parseStart = (s) => {
          if (!s) return '';
          if (/^\d+m\d+s$/.test(s)) {
            const m = s.match(/(\d+)m(\d+)s/);
            return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
          }
          if (/^\d+s$/.test(s)) return parseInt(s.replace('s',''), 10);
          if (/^\d+$/.test(s)) return parseInt(s, 10);
          return '';
        };

        let videoId = '';
        let start = '';

        if (hostname === 'youtube.com' || hostname === 'm.youtube.com') {
          // watch?v=ID
          videoId = u.searchParams.get('v') || '';
          start = u.searchParams.get('t') || u.searchParams.get('start') || '';
          // if it's already an embed path like /embed/ID
          if (!videoId && u.pathname.startsWith('/embed/')) {
            videoId = u.pathname.split('/embed/')[1] || '';
          }
        } else if (hostname === 'youtu.be') {
          videoId = u.pathname.replace('/', '') || '';
          start = u.searchParams.get('t') || u.searchParams.get('start') || '';
        } else if (u.pathname.startsWith('/embed/')) {
          // fallback for other hostnames where an embed path was given
          videoId = u.pathname.split('/embed/')[1] || '';
          start = u.searchParams.get('start') || '';
        }

        if (!videoId) {
          // if no video id could be parsed, return the original URL (best-effort)
          return url;
        }

        const params = new URLSearchParams();
        const parsedStart = parseStart(start);
        if (parsedStart) params.set('start', parsedStart);
        // enable JS API for better compatibility and add origin to avoid config errors
        params.set('enablejsapi', '1');
        try {
          if (typeof window !== 'undefined' && window.location && window.location.origin) {
            params.set('origin', window.location.origin);
          }
        } catch (e) {
          // ignore if window not available
        }

        return `https://www.youtube.com/embed/${videoId}${params.toString() ? `?${params.toString()}` : ''}`;
      } catch (e) {
        return url;
      }
    };


    if (!product) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-xl text-neutral-500">Product not found</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <div className="pb-32">
                <div className="max-w-7xl mx-auto px-6">
                    <motion.a 
                        href="/products"
                        className="inline-flex items-center gap-2 text-sm font-bold text-neutral-500 hover:text-black mb-12 transition-colors"
                        whileHover={{ x: -5 }}
                    >
                        <ArrowRight size={16} className="rotate-180" />
                        Back to Products
                    </motion.a>

                    <div className="grid md:grid-cols-2 gap-16 items-start mb-24">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="w-24 h-24 bg-neutral-100 rounded-2xl flex items-center justify-center text-black mb-8">
                                {product.icon}
                            </div>
                            <span className="px-3 py-1 bg-neutral-100 text-[10px] font-bold uppercase tracking-widest rounded-full text-neutral-500 inline-block mb-6">
                                {product.tag}
                            </span>
                            <h1 className="text-5xl md:text-6xl font-bold text-black mb-6 leading-tight">
                                {product.title}
                            </h1>
                            <p className="text-xl text-neutral-600 leading-relaxed mb-8">
                                {product.fullDesc}
                            </p>
                            {/* Primary CTA removed per request */}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="bg-neutral-50 rounded-3xl p-12 border border-neutral-200"
                        >
                            <h2 className="text-2xl font-bold mb-8">Key Features</h2>
                            <div className="space-y-4 mb-12">
                                {product.features.map((feature, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.2 + (idx * 0.05) }}
                                        className="flex items-start gap-3"
                                    >
                                        <div className="w-5 h-5 rounded-full bg-black flex items-center justify-center mt-1 flex-shrink-0">
                                            <div className="w-2 h-2 bg-white rounded-full" />
                                        </div>
                                        <span className="text-neutral-700">{feature}</span>
                                    </motion.div>
                                ))}
                            </div>
                            
                            {product.playStore && (
                                <div className="mt-8 pt-8 border-t border-neutral-200">
                                    <a
                                        href={product.playStore}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block hover:scale-[1.02] active:scale-95 transition-transform"
                                    >
                                        <img 
                                            src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                                            alt="Get it on Google Play" 
                                            className="h-14 w-auto drop-shadow-lg"
                                        />
                                    </a>
                                </div>
                            )}
                        </motion.div>
                    </div>
                    
                    {(product.youtubeUrl || product.youtubeUrls) && (
                        <div className="mt-16 border-t border-neutral-200 pt-16">
                            <h2 className="text-3xl font-bold mb-12">See It In Action</h2>
                            <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-8">
                                {(product.youtubeUrls || [product.youtubeUrl]).map((url, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-2xl bg-neutral-100"
                                    >
                                        <iframe
                                          width="100%"
                                          height="100%"
                                          src={toEmbedUrl(url)}
                                          title={`${product.title} Demo ${index + 1}`}
                                          frameBorder="0"
                                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                          referrerPolicy="strict-origin-when-cross-origin"
                                          allowFullScreen
                                          className="absolute inset-0"
                                        />
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const AnimatedCounter = ({ value, label, suffix = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = parseFloat(value);
      const duration = 2000;
      const increment = end / (duration / 16);
      
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(start);
        }
      }, 16);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);

  return (
    <div ref={ref} className="flex flex-col items-center">
      <div className="text-5xl md:text-7xl font-black tracking-tighter text-white mb-2">
        {count.toFixed(suffix === '%' ? 1 : 0)}{suffix}
      </div>
      <div className="text-xs font-mono text-neutral-400 uppercase tracking-[0.2em]">{label}</div>
    </div>
  );
};

const Services = () => {
  return (
    <section className="bg-white">
      {/* Hero */}
      <div className="relative pb-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-[40vw] h-[40vw] bg-neutral-100 rounded-full blur-3xl opacity-60 -translate-y-1/4 translate-x-1/4 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 border border-black/5 bg-neutral-50 px-3 py-1 rounded-full mb-6">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-600">Custom Tools for Market Professionals</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-semibold tracking-tight mb-6">Share your idea — we'll build it</h1>
            <p className="text-neutral-600 text-lg leading-relaxed max-w-xl">
              From lightweight desktop apps to mobile utilities, web platforms, or background automation scripts — we focus on building tools that are fast, focused, and tailored specifically to how you operate.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="/contact" className="bg-black text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-neutral-800 transition-colors">Share Your Idea</a>
              <a href="/products" className="px-6 py-3 rounded-full text-sm border border-neutral-200 hover:border-black transition-colors">View Products</a>
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="bg-white border border-neutral-200 rounded-2xl shadow-2xl p-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {['Desktop Apps','Web Platforms','Mobile Utilities','Automation Scripts'].map((x,i)=> (
                  <div key={i} className="border border-neutral-200 rounded-xl p-4 hover:shadow-md transition-all">
                    <div className="w-2 h-2 bg-black rounded-full mb-3" />
                    <div className="font-semibold">{x}</div>
                    <div className="text-neutral-500 mt-1">Built clean, fast and precise.</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What We Can Build */}
      <div className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12 items-start">
            <div className="lg:col-span-1">
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">What We Can Build</h2>
              <p className="text-neutral-600">We don't offer a template — we build tailored solutions from scratch based on your vision.</p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-8">
              {[
                'Desktop tools for monitoring markets or running custom logic',
                'Web apps for tracking trades, visualizing indicators, or sharing signals',
                'Mobile apps that let you manage your portfolio or get push notifications',
                'Automation scripts to clean data, generate alerts, or execute repeat tasks',
              ].map((item, i) => (
                <div key={i} className="border border-neutral-200 rounded-2xl p-6 hover:shadow-xl transition-all">
                  <div className="w-2 h-2 bg-black rounded-full mb-3" />
                  <div className="text-neutral-800">{item}</div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-neutral-700 mt-8 max-w-3xl">We work with traders and teams who want tools that just work — without bloat, friction, or distractions.</p>
        </div>
      </div>

      {/* Process */}
      <div className="py-24 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-12">Our Process: Built Around You</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {t:'You bring the idea',d:"Describe the workflow, signal, automation, or pain point you want to solve — even if it's just a rough idea."},
              {t:'We offer a solution',d:"Based on your input, we'll map out a clear approach, along with the time, cost, and delivery scope."},
              {t:'You approve',d:"Once everything's aligned, we begin development and keep you in the loop throughout the build."},
              {t:'You receive your custom tool',d:'Built for speed, clarity, and performance — and tested in your environment before delivery.'},
            ].map((step,i)=> (
              <div key={i} className="bg-white border border-neutral-200 rounded-2xl p-6">
                <div className="text-xs font-mono text-neutral-500 uppercase tracking-widest mb-2">Step {i+1}</div>
                <div className="text-xl font-semibold mb-2">{step.t}</div>
                <div className="text-neutral-600">{step.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why Work With Us */}
      <div className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">Why Work With Us?</h2>
          <p className="text-neutral-600 text-lg mb-12 max-w-3xl">Each solution is designed from the ground up, with a clear focus on performance, usability, and precision.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              'We understand market workflows — from day trading to swing setups and research-heavy investing.',
              'We communicate clearly and work quickly — no technical jargon, no delays.',
              'We focus on clean, efficient design — tools that do what you want, how you want.',
              "We only build what's needed — no fluff, no unnecessary extras.",
            ].map((point,i)=> (
              <div key={i} className="border border-neutral-200 rounded-2xl p-6 hover:shadow-lg transition-all">
                <div className="w-2 h-2 bg-black rounded-full mb-3" />
                <div className="text-neutral-800">{point}</div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-neutral-700 text-lg mb-2">You don't need to settle for "almost good enough."</p>
            <p className="text-neutral-900 font-semibold text-xl">Let's create something that actually solves the problem.</p>
          </div>
        </div>
      </div>

      {/* Ecosystem Section */}
      <div className="py-32 bg-black overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col items-center mb-10">
            <h2 className="text-white text-3xl md:text-5xl font-bold tracking-tight text-center mb-12">The Engineering Ecosystem</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 w-full max-w-4xl">
              <AnimatedCounter value="99.9" label="System Uptime" suffix="%" />
              <AnimatedCounter value="24" label="Execution Latency" suffix="ms" />
            </div>
          </div>

          {/* Tech Nebula */}
          <div className="py-10 flex flex-wrap justify-center gap-4 md:gap-6">
            {[
              { n: 'Python', c: 'bg-blue-500/20 border-blue-500/30' },
              { n: 'HTML', c: 'bg-orange-500/20 border-orange-500/30' },
              { n: 'CSS', c: 'bg-blue-400/20 border-blue-400/30' },
              { n: 'JavaScript', c: 'bg-yellow-500/20 border-yellow-500/30' },
              { n: 'MySQL', c: 'bg-blue-600/20 border-blue-600/30' },
              { n: 'MongoDB', c: 'bg-green-600/20 border-green-600/30' },
              { n: 'PostgreSQL', c: 'bg-indigo-600/20 border-indigo-600/30' }
            ].map((tech, i) => (
              <motion.div
                key={i}
                animate={{ 
                  y: [0, -15, 0],
                  rotate: i % 2 === 0 ? [0, 5, 0] : [0, -5, 0]
                }}
                transition={{ 
                  duration: 4 + (i % 3), 
                  repeat: Infinity, 
                  ease: "easeInOut",
                  delay: i * 0.2
                }}
                className={`px-6 py-3 border rounded-full font-mono text-sm font-bold text-white tracking-widest shadow-xl shadow-white/5 ${tech.c}`}
              >
                {tech.n}
              </motion.div>
            ))}
          </div>

          {/* Pipeline */}
          <div className="mt-8 border-t border-white/10 pt-12">
             <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {[
                  { t: 'Architecture & Design', d: 'Scalable structural patterns for high-throughput data.', i: <Layers size={24} /> },
                  { t: 'High-Performance Coding', d: 'Clean, optimized logic built for milliseconds.', i: <Code2 size={24} /> },
                  { t: 'Stress Testing', d: 'Validating robustness under extreme market volatility.', i: <Activity size={24} /> },
                  { t: 'Seamless Deployment', d: 'Zero-downtime integration for active environments.', i: <Workflow size={24} /> }
                ].map((step, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ y: -5 }}
                    className="group bg-neutral-900 border border-white/5 shadow-2xl p-8 rounded-3xl hover:border-white/20 transition-all duration-500"
                  >
                    <div className="w-12 h-12 bg-white/5 text-white/50 rounded-2xl flex items-center justify-center mb-6 border border-white/10 group-hover:bg-white group-hover:text-black transition-all">
                      {step.i}
                    </div>
                    <div className="text-white text-lg font-bold mb-3">{step.t}</div>
                    <div className="text-neutral-500 text-sm leading-relaxed">{step.d}</div>
                    <div className="mt-6 flex items-center gap-2 opacity-20">
                      <div className="w-full h-px bg-white/20" />
                      <div className="text-[10px] font-mono text-white">0{i+1}</div>
                    </div>
                  </motion.div>
                ))}
             </div>
          </div>
        </div>
      </div>
    </section>
  );
};
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');
    setSubmitError('');

    const phoneDigits = formData.phone.trim().replace(/\s/g, '');
    if (!/^\d{10}$/.test(phoneDigits)) {
      setSubmitError('Phone number must be exactly 10 digits.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/contact/submit/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, phone: phoneDigits }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const detail =
          typeof data === 'object' && data
            ? Object.values(data).flat().join(' ')
            : 'Something went wrong.';
        setSubmitError(detail);
        return;
      }

      setSubmitMessage(data.message || 'Message sent successfully.');
      setFormData({ name: '', phone: '', email: '', company: '', subject: '', message: '' });
    } catch (err) {
      setSubmitError('Unable to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="pb-32 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 text-black">Contact Us</h1>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed">
            Have questions? We're just one message away. Contact us for product help, partnership opportunities, or custom software inquiries.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="bg-neutral-50 rounded-2xl p-6 sm:p-8 border border-neutral-200">
              <h2 className="text-2xl font-bold mb-6 text-black">Get In Touch</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white" size={20} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-neutral-500 uppercase tracking-wider mb-1">Email</div>
                    <a href="mailto:arhamtechnologyindia@gmail.com" className="text-base sm:text-lg font-semibold text-black hover:underline break-all block">
                      arhamtechnologyindia@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="text-white" size={20} />
                  </div>
                  <div>
                    <div className="text-sm text-neutral-500 uppercase tracking-wider mb-1">Phone</div>
                    <a href="tel:+917228882088" className="text-lg font-semibold text-black hover:underline">
                      +91-7228882088
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-white" size={20} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-neutral-500 uppercase tracking-wider mb-1">Address</div>
                    <p className="text-base sm:text-lg font-semibold text-black leading-snug">
                      301-303, Pramukh Tangent, KH-0,<br />
                      Gandhinagar, Gujarat, India - 382421
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className="rounded-2xl overflow-hidden border border-neutral-200 h-64 lg:h-[475px]">
              <iframe
                title="ARHAM FinTech Office Location"
                src="https://maps.google.com/maps?q=Pramukh+Tangent+KH-0+Gandhinagar+Gujarat+382421+India&output=embed&z=16"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 rounded-2xl p-6 sm:p-8 space-y-6">
              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:border-black transition-colors"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-black mb-2">Phone Number <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value="+91"
                    disabled
                    className="w-20 px-4 py-3 border border-neutral-300 rounded-lg bg-neutral-50 text-neutral-500"
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    maxLength={10}
                    className="flex-1 px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:border-black transition-colors"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-black mb-2">Your Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:border-black transition-colors"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-black mb-2">Your Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:border-black transition-colors"
                  placeholder="Enter your company name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-black mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:border-black transition-colors"
                  placeholder="Enter subject"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-black mb-2">Your Question</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:border-black transition-colors resize-none"
                  placeholder="Enter your message"
                />
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSubmitting}
                className="w-full bg-black text-white py-4 rounded-full font-semibold text-lg hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending…' : 'Send Message'}
              </motion.button>

              {submitMessage && (
                <p className="text-sm text-green-600" role="status">{submitMessage}</p>
              )}
              {submitError && (
                <p className="text-sm text-red-600" role="alert">{submitError}</p>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};


const ImageAutoSlider = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const images = Array.from({ length: 11 }, (_, i) => `/static/office/${i + 1}.webp`);
  const duplicatedImages = [...images, ...images];

  return (
    <>
      <style>{`
        @keyframes scroll-right {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .infinite-scroll { animation: scroll-right 40s linear infinite; }
        .scroll-container {
          mask: linear-gradient(90deg, transparent 0%, black 10%, black 90%, transparent 100%);
          -webkit-mask: linear-gradient(90deg, transparent 0%, black 10%, black 90%, transparent 100%);
        }
        .image-item { transition: transform 0.3s ease, filter 0.3s ease; cursor: pointer; }
        .image-item:hover { transform: scale(1.05); filter: brightness(1.1); }
      `}</style>
      
      <div className="w-full relative overflow-hidden flex items-center justify-center py-10">        
        <div className="relative z-10 w-full flex items-center justify-center">
          <div className="scroll-container w-full">
            <div className="flex gap-6 w-max infinite-scroll">
                {duplicatedImages.map((image, index) => (
                  <div
                    key={index}
                    className="image-item flex-shrink-0 w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 rounded-xl overflow-hidden shadow-2xl border border-neutral-100 relative group"
                    onClick={() => setSelectedImage(image)}
                  >
                    <img
                      src={image}
                      alt={`Office layout ${(index % images.length) + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center z-10">
                      <div className="bg-white/90 backdrop-blur-sm text-black px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg">
                        View
                      </div>
                    </div>
                  </div>
                ))}
              </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.button 
              className="absolute top-8 right-8 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X size={32} />
            </motion.button>
            <motion.img
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              src={selectedImage}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const OfficeGallery = () => {
  return (
    <section className="mt-16 bg-neutral-50 border-y border-neutral-100 overflow-hidden">
      <div className="pt-24 pb-12 max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-xs font-mono text-neutral-500 uppercase tracking-widest mb-3">Workspace</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-black">Life at ARHAM FinTech</h2>
          </motion.div>
          <p className="text-neutral-500 max-w-sm text-sm italic">
            A glimpse into our creative workspace, where precision meets innovation to build the future of fintech.
          </p>
        </div>
      </div>

      <ImageAutoSlider />
    </section>
  );
};


const Company = () => {
    return (
        <section className="pb-32 bg-white relative overflow-hidden">
            <div className="max-w-4xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                  <h1 className="text-5xl md:text-6xl font-bold mb-6 text-black">About Us</h1>
                  <p className="text-lg text-neutral-700 leading-relaxed mb-10">
                    Founded in 2014 by Tejas Sanghvi, ADWEALTH PRIVATE LIMITED established the Arham Fintech brand with a simple belief that financial software should be powerful, accurate, and user-focused.
                  </p>

                  <div className="space-y-10 text-lg text-neutral-600 leading-relaxed">
                    <div className="space-y-4">
                      <h2 className="text-2xl font-bold text-black">Who We Are</h2>
                      <p>
                        Arham Fintech is a fintech brand owned and operated by ADWEALTH PRIVATE LIMITED. We specialize in custom-built and ready-to-use software solutions for the stock trading ecosystem. Our products support automation, analytics, and decision-making across desktop, mobile, and web platforms.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <h2 className="text-2xl font-bold text-black">Our Mission</h2>
                      <p>
                        To provide reliable, intuitive, and scalable software solutions that streamline trading workflows and enhance user productivity.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <h2 className="text-2xl font-bold text-black">Our Vision</h2>
                      <p>
                        To be recognized as a trusted partner for traders and institutions seeking high-performance, market-focused tools and custom development.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <h2 className="text-2xl font-bold text-black">Legal Information</h2>
                      <p>
                        Arham Fintech is a brand operated by ADWEALTH PRIVATE LIMITED, the legal entity responsible for the development, operation, and support of the products and services offered through this website.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <p className="text-neutral-700 font-semibold">
                        We don't just write code—we solve real problems for real market participants.
                      </p>
                      <p>
                        Whether you're looking for a ready-made tool or a custom solution, we're here to help.
                      </p>
                    </div>

                  </div>
                </motion.div>
              </div>

              <OfficeGallery />
            </section>
          );
        };

const LaptopAnimation = () => {
  const [phase, setPhase] = useState('closed'); // closed, opening, interacting, clicking, success

  useEffect(() => {
    let isMounted = true;
    const sequence = async () => {
      while (isMounted) {
        // 1. Start Closed
        setPhase('closed');
        await new Promise(r => setTimeout(r, 1000));
        if (!isMounted) break;
        
        // 2. Open Laptop
        setPhase('opening');
        await new Promise(r => setTimeout(r, 1500));
        if (!isMounted) break;
        
        // 3. Move Cursor
        setPhase('interacting');
        await new Promise(r => setTimeout(r, 2000));
        if (!isMounted) break;
        
        // 4. Click
        setPhase('clicking');
        await new Promise(r => setTimeout(r, 500));
        if (!isMounted) break;
        
        // 5. Success
        setPhase('success');
        await new Promise(r => setTimeout(r, 3000));
      }
    };
    
    sequence();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="relative w-full h-[500px] flex items-center justify-center perspective-1000">
      <motion.div 
        animate={{ 
          rotateY: phase === 'closed' ? 0 : [-5, 5, -5],
          y: phase === 'closed' ? 40 : 0
        }}
        transition={{ 
          rotateY: { duration: 6, repeat: Infinity, ease: "easeInOut" },
          y: { duration: 0.8 }
        }}
        style={{ transformStyle: "preserve-3d" }}
        className="relative w-80 h-56"
      >
        {/* Laptop Lid (Screen & Cover) */}
        <motion.div
          initial={{ rotateX: -95 }}
          animate={{ 
            rotateX: (phase === 'closed') ? -95 : 0 
          }}
          transition={{ 
            type: "spring", 
            stiffness: 45, 
            damping: 14
          }}
          style={{ 
            transformOrigin: "bottom",
            transformStyle: "preserve-3d",
            bottom: "50%"
          }}
          className="absolute inset-x-0 h-full z-20"
        >
          {/* Inner Screen Side */}
          <div 
            style={{ backfaceVisibility: "hidden" }}
            className="absolute inset-0 bg-neutral-900 rounded-xl border-[6px] border-neutral-300 shadow-2xl overflow-hidden flex flex-col items-center justify-center p-1"
          >
            <div className="w-full h-full bg-white relative flex flex-col items-center justify-center p-4 rounded-sm">
              {/* Browser interface */}
              <div className="absolute top-0 inset-x-0 h-6 bg-neutral-50 flex items-center px-4 gap-1.5 border-b border-neutral-200">
                 {[...Array(3)].map((_, i) => (
                   <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === 0 ? 'bg-red-400' : i === 1 ? 'bg-yellow-400' : 'bg-green-400'}`} />
                 ))}
                 <div className="ml-4 h-3 w-32 bg-white rounded-full border border-neutral-200" />
              </div>

              {/* Main Content (Hidden on Success) */}
              <div className={`transition-all duration-700 flex flex-col items-center ${phase === 'success' ? 'opacity-0 scale-90' : 'opacity-100 scale-100'}`}>
                <div className="w-24 h-3 bg-neutral-100 rounded-full mb-4" />
                <div className="w-40 h-2 bg-neutral-100 rounded-full mb-2" />
                <div className="w-32 h-2 bg-neutral-100 rounded-full mb-10" />
                
                <motion.div 
                  animate={{ scale: phase === 'clicking' ? 0.9 : 1 }}
                  className="px-6 py-2.5 bg-black text-white text-[10px] font-bold rounded-full tracking-widest uppercase shadow-xl"
                >
                   Apply Now
                </motion.div>
              </div>

              {/* Success Content */}
              <div className={`absolute inset-0 bg-white flex flex-col items-center justify-center transition-all duration-700 ${phase === 'success' ? 'opacity-100 scale-100' : 'opacity-0 scale-90 pointer-events-none'}`}>
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: phase === 'success' ? 1 : 0 }}
                  className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-6 text-white shadow-2xl"
                >
                  <Check size={32} strokeWidth={3} />
                </motion.div>
                <div className="text-center">
                  <p className="text-[12px] font-black uppercase tracking-widest text-black mb-1 leading-none">Accepted</p>
                  <p className="text-[9px] font-bold text-neutral-500 uppercase tracking-widest">Welcome to the team</p>
                </div>
              </div>

              {/* Cursor */}
              <motion.div
                initial={{ x: 80, y: 100, opacity: 0 }}
                animate={{ 
                  x: (phase === 'interacting' || phase === 'clicking') ? 0 : 80,
                  y: (phase === 'interacting' || phase === 'clicking') ? 38 : 100,
                  opacity: (phase === 'interacting' || phase === 'clicking') ? 1 : 0
                }}
                transition={{ 
                  duration: phase === 'interacting' ? 1.5 : 0.2, 
                  ease: "easeInOut" 
                }}
                className="absolute z-30 pointer-events-none"
              >
                <MousePointer2 className="text-black rotate-[-15deg] fill-white drop-shadow-xl" size={24} />
              </motion.div>
            </div>
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-neutral-800 rounded-full" />
          </div>

          {/* Outer Cover Side (Silver Finish) */}
          <div 
            style={{ 
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)" 
            }}
            className="absolute inset-0 bg-neutral-300 rounded-xl border-[6px] border-neutral-200 flex items-center justify-center shadow-2xl"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-white/10 via-transparent to-white/20" />
            <div className="w-16 h-16 rounded-full bg-neutral-400/20 backdrop-blur-md border border-white/20 flex items-center justify-center">
               <div className="w-6 h-6 bg-neutral-400 rounded-md rotate-12 opacity-50" />
            </div>
          </div>
        </motion.div>

        {/* Junction / Hinge bar */}
        <div className="absolute top-1/2 left-0 right-0 h-4 bg-neutral-400 -translate-y-1/2 rounded-full z-10 shadow-inner overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-transparent" />
        </div>

        {/* Laptop Base (Body - Silver Finish) */}
        <div 
          style={{ 
            transform: "rotateX(75deg)",
            transformOrigin: "top",
            top: "50%"
          }}
          className="absolute inset-x-0 h-full bg-neutral-300 rounded-b-2xl border-t border-neutral-100 shadow-2xl p-2"
        >
          <div className="w-full h-full bg-neutral-200 rounded-b-xl p-4 flex flex-col gap-4 shadow-inner">
            {/* Detailed Black Keyboard Keys */}
            <div className="grid grid-cols-12 gap-1.5 px-2">
               {[...Array(60)].map((_, i) => (
                 <div 
                  key={i} 
                  className={`bg-neutral-900 rounded-[2px] h-3.5 border-b-2 border-black/80 shadow-md ${
                    i === 54 ? 'col-span-5' : 'col-span-1'
                  }`} 
                 />
               ))}
            </div>
            {/* Recessed Trackpad */}
            <div className="mx-auto w-32 h-20 bg-neutral-300 rounded-lg border border-neutral-400/50 shadow-inner mt-auto relative overflow-hidden">
               <div className="absolute top-0 inset-x-0 h-px bg-white/40" />
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const SKILLS = ['Python', 'PHP', 'HTML/CSS', 'SQL', 'Github', 'JavaScript', 'Node JS', 'Next JS'];
const PROFICIENCIES = ['Unknown', 'Beginner', 'Intermediate', 'Advanced'];
const PASSOUT_OPTIONS = ['Diploma Computer/IT', 'B.E./B.TECH', 'Msc.IT/MCA', 'Bsc.IT/BCA', 'Other'];

const JobApplicationModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    gender: '',
    current_address: '',
    twelfth_percentage: '',
    college_name: '',
    college_cgpa: '',
    programming_skills: {},
    job_experience_years: '',
    pass_out: '',
    achievements: '',
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSkillChange = (skill, proficiency) => {
    setFormData(prev => ({
      ...prev,
      programming_skills: { ...prev.programming_skills, [skill]: proficiency },
    }));
  };

  const validateStep1 = () => {
    const { full_name, phone, email, gender, current_address, twelfth_percentage, college_name, college_cgpa } = formData;
    if (!full_name || !phone || !email || !gender || !current_address || !twelfth_percentage || !college_name || !college_cgpa) {
      setSubmitError('Please fill all required fields.');
      return false;
    }
    if (!/^\d{10}$/.test(phone.trim().replace(/\s/g, ''))) {
      setSubmitError('Phone number must be exactly 10 digits.');
      return false;
    }
    setSubmitError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setIsSubmitting(true);
    try {
      const phoneDigits = formData.phone.trim().replace(/\s/g, '');
      const response = await fetch('/careers/apply/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, phone: phoneDigits }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = typeof data === 'object' && data
          ? Object.values(data).flat().join(' ')
          : 'Something went wrong.';
        setSubmitError(detail);
        return;
      }
      onSuccess();
    } catch {
      setSubmitError('Unable to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-8 py-6 flex justify-between items-center z-10">
          <div>
            <span className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase">Application · Step {step} of 2</span>
            <h2 className="text-2xl font-black tracking-tighter mt-1">
              {step === 1 ? 'Personal Information' : 'Technical Background'}
            </h2>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-black transition-colors p-1">
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-8">
            {step === 1 && (
              <div className="flex flex-col gap-6">
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-2">Full Name *</label>
                  <input name="full_name" value={formData.full_name} onChange={handleChange} required
                    className="w-full border border-neutral-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-2">Phone Number *</label>
                  <div className="flex">
                    <span className="border border-r-0 border-neutral-200 px-3 py-3 text-sm text-neutral-500 bg-neutral-50 select-none">+91</span>
                    <input name="phone" value={formData.phone} onChange={handleChange} required maxLength={10} placeholder="10 digit number"
                      className="flex-1 border border-neutral-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-2">Email ID *</label>
                  <input name="email" type="email" value={formData.email} onChange={handleChange} required
                    className="w-full border border-neutral-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-2">Gender *</label>
                  <div className="flex gap-6 mt-1">
                    {['Male', 'Female'].map(g => (
                      <label key={g} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="radio" name="gender" value={g} checked={formData.gender === g} onChange={handleChange} className="accent-black" />
                        {g}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-2">Current Address *</label>
                  <textarea name="current_address" value={formData.current_address} onChange={handleChange} required rows={3}
                    className="w-full border border-neutral-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors resize-none" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-2">12th Percentage *</label>
                    <input name="twelfth_percentage" value={formData.twelfth_percentage} onChange={handleChange} required placeholder="e.g. 85.5"
                      className="w-full border border-neutral-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-2">College CGPA *</label>
                    <input name="college_cgpa" value={formData.college_cgpa} onChange={handleChange} required placeholder="e.g. 8.5"
                      className="w-full border border-neutral-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-2">Name of College *</label>
                  <input name="college_name" value={formData.college_name} onChange={handleChange} required
                    className="w-full border border-neutral-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col gap-8">
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-3">Programming Skills *</label>
                  <div className="border border-neutral-200 overflow-hidden text-sm">
                    <div className="grid grid-cols-5 bg-neutral-50 border-b border-neutral-200">
                      <div className="px-3 py-2 font-mono text-[9px] tracking-widest text-neutral-500 uppercase">Skill</div>
                      {PROFICIENCIES.map(p => (
                        <div key={p} className="py-2 font-mono text-[9px] tracking-widest text-neutral-500 uppercase text-center">{p}</div>
                      ))}
                    </div>
                    {SKILLS.map((skill, i) => (
                      <div key={skill} className={`grid grid-cols-5 border-b border-neutral-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'}`}>
                        <div className="px-3 py-3 font-medium">{skill}</div>
                        {PROFICIENCIES.map(p => (
                          <div key={p} className="flex items-center justify-center py-3">
                            <input type="radio" name={`skill_${skill}`} value={p}
                              checked={formData.programming_skills[skill] === p}
                              onChange={() => handleSkillChange(skill, p)}
                              className="accent-black cursor-pointer" />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-2">Experience (Years) *</label>
                    <input name="job_experience_years" value={formData.job_experience_years} onChange={handleChange} required
                      type="number" min="0" max="50" step="0.5" placeholder="e.g. 1.5"
                      className="w-full border border-neutral-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-2">Qualification *</label>
                    <select name="pass_out" value={formData.pass_out} onChange={handleChange} required
                      className="w-full border border-neutral-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white">
                      <option value="">Select</option>
                      {PASSOUT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase block mb-2">
                    Achievements <span className="text-neutral-400 normal-case">(Optional)</span>
                  </label>
                  <textarea name="achievements" value={formData.achievements} onChange={handleChange} rows={4}
                    placeholder="Tell us about your projects, achievements, or anything that makes you stand out..."
                    className="w-full border border-neutral-200 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors resize-none" />
                </div>
              </div>
            )}

            {submitError && (
              <p className="text-red-600 text-sm mt-4">{submitError}</p>
            )}

            <div className="flex justify-between items-center mt-8 pt-6 border-t border-neutral-200">
              {step === 2 ? (
                <button type="button" onClick={() => { setStep(1); setSubmitError(''); }}
                  className="font-mono text-xs tracking-widest uppercase text-neutral-500 hover:text-black transition-colors">
                  ← BACK
                </button>
              ) : <span />}
              {step === 1 ? (
                <button type="button" onClick={handleNext}
                  className="bg-black text-white px-10 py-4 font-mono text-xs tracking-widest uppercase hover:bg-neutral-800 transition-colors">
                  NEXT →
                </button>
              ) : (
                <button type="submit" disabled={isSubmitting}
                  className="bg-black text-white px-10 py-4 font-mono text-xs tracking-widest uppercase hover:bg-neutral-800 transition-colors disabled:opacity-50">
                  {isSubmitting ? 'SUBMITTING...' : 'SUBMIT APPLICATION'}
                </button>
              )}
            </div>
          </form>
      </div>
    </div>
  );
};

const Careers = () => {
  const [showModal, setShowModal] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  return (
    <>
      {showModal && <JobApplicationModal onClose={() => setShowModal(false)} onSuccess={() => { setHasApplied(true); setShowModal(false); }} />}
      <style>{`
        @keyframes scan-line {
            0% { transform: translateY(-100%); opacity: 0; }
            50% { opacity: 0.5; }
            100% { transform: translateY(400%); opacity: 0; }
        }
        .animate-scan {
            animation: scan-line 4s linear infinite;
        }
        .dot-grid {
            background-image: radial-gradient(circle, #000 1px, transparent 1px);
            background-size: 32px 32px;
        }
      `}</style>
      <section className="bg-white text-black font-sans selection:bg-black selection:text-white pt-24 pb-12">
        <main className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <section className="flex flex-col md:flex-row items-center px-6 md:px-12 pb-24 gap-12 overflow-hidden">
            <div className="w-full md:w-1/2 flex flex-col items-start gap-8">
              <div className="flex flex-col">
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-7xl md:text-[9rem] font-black leading-[0.85] tracking-tighter text-black"
                >
                  WE ARE<br/>HIRING
                </motion.h1>
              </div>
              <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-col gap-6 max-w-lg mt-8"
              >
                <p className="text-neutral-600 text-lg">
                  Join our mission to revolutionize the stock market software space.
                </p>
              </motion.div>
            </div>
            
            {/* Hiring Visual (Animated Component) */}
            <motion.div 
               initial={{ opacity: 0, scale: 0.95 }}
               animate={{ opacity: 1, scale: 1 }}
               transition={{ duration: 0.6, delay: 0.4 }}
               className="w-full md:w-1/2 h-[600px] relative flex flex-col items-center justify-center bg-neutral-100 rounded-3xl overflow-hidden"
            >
              <div className="absolute inset-0 dot-grid opacity-10"></div>
              <LaptopAnimation />
            </motion.div>
          </section>

          {/* Information Grid */}
          <section className="bg-neutral-100 px-6 md:px-12 py-32">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-neutral-200">
              {/* Qualifications */}
              <div className="bg-white p-12 md:p-20">
                <h3 className="font-sans text-2xl tracking-[0.2em] uppercase font-bold text-black mb-16 flex items-center gap-4">
                  <span className="w-3 h-3 bg-black rounded-full"></span> QUALIFICATIONS
                </h3>
                <ul className="flex flex-col gap-8">
                  <li className="flex flex-col group">
                    <span className="text-2xl font-bold tracking-tight">Diploma IT/CS</span>
                    <span className="text-xs font-mono text-neutral-500 uppercase tracking-widest mt-1">Foundational Engineering</span>
                  </li>
                  <li className="flex flex-col group">
                    <span className="text-2xl font-bold tracking-tight">B.E. CS/IT</span>
                    <span className="text-xs font-mono text-neutral-500 uppercase tracking-widest mt-1">Bachelor of Engineering</span>
                  </li>
                  <li className="flex flex-col group">
                    <span className="text-2xl font-bold tracking-tight">B.Tech IT/CS</span>
                    <span className="text-xs font-mono text-neutral-500 uppercase tracking-widest mt-1">Technical Specialization</span>
                  </li>
                  <li className="flex flex-col group">
                    <span className="text-2xl font-bold tracking-tight">MCA</span>
                    <span className="text-xs font-mono text-neutral-500 uppercase tracking-widest mt-1">Masters in Computer App.</span>
                  </li>
                  <li className="flex flex-col group">
                    <span className="text-2xl font-bold tracking-tight">MSC IT/CS</span>
                    <span className="text-xs font-mono text-neutral-500 uppercase tracking-widest mt-1">Scientific Research Path</span>
                  </li>
                </ul>
              </div>
              
              {/* Tech Stack */}
              <div className="bg-white p-12 md:p-20">
                <h3 className="font-sans text-2xl tracking-[0.2em] uppercase font-bold text-black mb-16 flex items-center gap-4">
                  <span className="w-3 h-3 bg-black rounded-full"></span> TECH STACK
                </h3>
                <ul className="flex flex-col gap-8">
                  {[
                    { name: 'Python', sub: 'Backend & Automation' },
                    { name: 'PHP', sub: 'Server-Side Scripting' },
                    { name: 'HTML/CSS', sub: 'UI Markup & Styling' },
                    { name: 'Node Js', sub: 'Runtime Environment' },
                    { name: 'React Native', sub: 'Cross-Platform Mobile' },
                    { name: 'Next JS', sub: 'Full-Stack Framework' },
                    { name: 'Flutter', sub: 'Native App Development' },
                  ].map(({ name, sub }) => (
                    <li key={name} className="flex flex-col group">
                      <span className="text-2xl font-bold tracking-tight">{name}</span>
                      <span className="text-xs font-mono text-neutral-500 uppercase tracking-widest mt-1">{sub}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-16 pt-8 border-t border-neutral-100">
                  <p className="text-sm text-neutral-600 leading-relaxed italic">
                    We're looking for people who can build, break, learn, and grow. At ARHAM FinTech we're not just building tools—we're crafting intelligent, automated, and scalable products for the Indian stock market ecosystem.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA / Contact Section */}
          <section className="px-6 md:px-12 py-32 border-t border-neutral-200 bg-white">
            <div className="flex flex-col md:flex-row justify-between items-start gap-16">
              <div className="max-w-2xl">
                <h2 className="text-5xl md:text-6xl font-black tracking-tighter mb-8">BUILD THE FUTURE OF FINTECH.</h2>
                <p className="text-neutral-600 leading-relaxed text-lg mb-12">
                  We are looking for ambitious individuals who are ready to push the boundaries of financial technology. Join ARHAM FinTech and start your career in an environment that values innovation and precision. We're a fast-growing team solving unique tech challenges for real financial use-cases.
                  <br/><br/>
                  Let's build something great, together.
                </p>
                <div className="flex flex-wrap gap-4">
                  <button onClick={() => !hasApplied && setShowModal(true)}
                    className={`px-10 py-5 font-mono text-xs tracking-widest uppercase transition-colors rounded-sm ${hasApplied ? 'bg-neutral-200 text-neutral-500 cursor-default' : 'bg-black text-white hover:bg-neutral-800 cursor-pointer'}`}>
                    {hasApplied ? 'APPLIED ✓' : 'APPLY NOW'}
                  </button>
                  <a 
                    href="/static/Hiring%20Brochure.pdf"
                    download="ARHAM_FinTech_Hiring_Brochure.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-black text-black px-10 py-5 font-mono text-xs tracking-widest uppercase hover:bg-neutral-50 transition-colors rounded-sm inline-block"
                  >
                    DOWNLOAD BROCHURE
                  </a>
                </div>
              </div>
              <div className="w-full md:w-1/3 flex flex-col gap-12 bg-white p-12 shadow-[40px_40px_60px_-15px_rgba(0,0,0,0.05)] border border-neutral-100">
                <div className="flex flex-col gap-2">
                  <span className="font-mono text-[10px] tracking-widest text-neutral-500 uppercase">Information</span>
                  <h4 className="text-2xl font-bold">Contact Details</h4>
                </div>
                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <Mail className="text-black mt-1" size={24} />
                    <div className="flex flex-col">
                      <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-widest mb-1">Email</span>
                      <a className="text-sm font-bold border-b border-transparent hover:border-black transition-all" href="mailto:arhamtechnologyindia@gmail.com">arhamtechnologyindia@gmail.com</a>
                      <p className="text-xs text-neutral-500 mt-2">Include your resume, project samples, and anything that tells us why you're awesome.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone className="text-black mt-1" size={24} />
                    <div className="flex flex-col">
                      <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-widest mb-1">Contact Number</span>
                      <a className="text-sm font-bold" href="tel:+917228882088">+91 7228882088</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="text-black mt-1" size={24} />
                    <div className="flex flex-col">
                      <span className="font-mono text-[10px] text-neutral-500 uppercase tracking-widest mb-1">Office Address</span>
                      <span className="text-sm font-bold leading-relaxed">301-303, Pramukh Tangent, KH-0,
                      <br/>Gandhinagar, Gujarat, India-382421</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </section>
    </>
  );
};

// --- Hybrid Home Sections ---

const ServicesPreview = () => (
    <section id="services" className="pb-32 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-4">Our Services</h2>
                    <p className="text-neutral-500 text-lg max-w-xl">
                        Custom tools and precision engineering for the financial markets.
                    </p>
                </motion.div>
                <a href="/services" className="hidden md:flex items-center gap-2 group text-sm font-bold uppercase tracking-widest text-neutral-400 hover:text-black transition-all">
                    View All Details <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </a>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
                {[
                    { title: "Custom Algo Development", desc: "High-frequency strategies and algorithmic execution modules built for speed and precision.", icon: <Cpu /> },
                    { title: "FinTech Solutions", desc: "Enterprise-grade software architecture designed for robust market operations.", icon: <ShieldCheck /> },
                    { title: "Custom Trading Tools", desc: "Bespoke utilities for active traders — from data scrapers to signal dashboards.", icon: <Zap /> }
                ].map((s, i) => (
                    <motion.div 
                        key={i}
                        className="bg-neutral-50 p-8 rounded-3xl border border-neutral-100 hover:border-black/10 transition-all hover:bg-white hover:shadow-2xl hover:shadow-black/5"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        viewport={{ once: true }}
                    >
                        <div className="w-12 h-12 bg-black text-white rounded-2xl flex items-center justify-center mb-6">
                            {s.icon}
                        </div>
                        <h3 className="text-xl font-bold mb-3">{s.title}</h3>
                        <p className="text-neutral-500 text-sm leading-relaxed">{s.desc}</p>
                    </motion.div>
                ))}
            </div>
            
            <div className="mt-12 md:hidden">
              <a href="/services" className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-black">
                  View All Details <ArrowRight size={16} />
              </a>
            </div>
        </div>
    </section>
);

const ScrollingProducts = () => {
    const products = [
        { id: "ipo-utility", title: "IPO Utility", desc: "Smart IPO Premium Management" },
        { id: "bounceback-trading", title: "Bounceback Trading", desc: "Stock circuit monitoring suite" },
        { id: "pair-trading", title: "Pair Trading", desc: "Professional grade strategy execution" },
        { id: "corporate-actions", title: "Corporate Actions", desc: "AI Summarized market updates" },
        { id: "ofs-cut-off", title: "OFS Predictor", desc: "Real-time demand and bid calculation" },
        { id: "ipo-allotment", title: "IPO Allotment", desc: "Bulk PAN checking tool" }
    ];

    return (
        <section id="products" className="py-32 bg-black overflow-hidden relative">
            <div className="absolute top-0 left-0 w-full h-px bg-neutral-800" />
            <div className="max-w-7xl mx-auto px-6 mb-12">
                <h2 className="text-white text-3xl font-bold tracking-tight">Our Work in Action</h2>
            </div>
            
            <motion.div 
                className="flex whitespace-nowrap gap-8 px-6"
                animate={{ x: ["0%", "-50%"] }}
                transition={{ duration: 40, ease: "linear", repeat: Infinity }}
            >
                {[...products, ...products, ...products].map((p, i) => (
                    <a 
                      key={i} 
                      href={`/product/${p.id}`}
                      className="inline-block min-w-[280px] sm:min-w-[320px] bg-neutral-900 border border-neutral-800 p-8 rounded-3xl hover:border-white/20 transition-all group"
                    >
                        <div className="text-white text-xl font-bold mb-2 group-hover:text-blue-400 transition-colors uppercase tracking-tight">{p.title}</div>
                        <div className="text-neutral-500 text-sm whitespace-normal leading-relaxed">{p.desc}</div>
                    </a>
                ))}
            </motion.div>
        </section>
    );
};

const CompanyPreview = () => {
    const images = Array.from({ length: 11 }, (_, i) => `/static/office/${i + 1}.webp`);
    const [currentImg, setCurrentImg] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImg((prev) => (prev + 1) % images.length);
        }, 4000);
        return () => clearInterval(interval);
    }, [images.length]);

    return (
        <section id="company" className="pb-0 bg-white relative overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                >
                    <p className="text-xs font-mono text-neutral-500 uppercase tracking-widest mb-3">About Arham FinTech</p>
                    <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-8">Why Arham FinTech?</h2>
                    <div className="space-y-8">
                        {[
                            { t: "Market Expertise", d: "Not just coders. We are market participants who understand the nuances of order flow, slippage, and execution logic." },
                            { t: "High Performance", d: "In fintech, milliseconds matter. Our software is engineered for high throughput and ultra-low latency." },
                            { t: "Professional Support", d: "When you build custom tools with us, you get direct access to the engineers who wrote the code." }
                        ].map((item, i) => (
                            <div key={i} className="flex gap-4 group">
                                <div className="w-8 h-8 rounded-xl bg-neutral-100 text-black flex items-center justify-center text-xs font-bold group-hover:bg-black group-hover:text-white transition-colors shrink-0 mt-1">{i+1}</div>
                                <div>
                                    <h4 className="font-bold text-lg mb-1">{item.t}</h4>
                                    <p className="text-neutral-500 text-sm leading-relaxed">{item.d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <div className="relative h-[400px] md:h-[500px] rounded-[3rem] overflow-hidden shadow-2xl border border-neutral-100 group">
                    <AnimatePresence mode="wait">
                        <motion.img
                            key={currentImg}
                            src={images[currentImg]}
                            initial={{ opacity: 0, scale: 1.1 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 1 }}
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                    </AnimatePresence>
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500" />
                    <div className="absolute bottom-6 left-6 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 text-[10px] text-white font-mono uppercase tracking-widest">
                        Live Workspace Gallery
                    </div>
                </div>
            </div>
        </section>
    );
};

const CareersTeaser = () => (
  <section id="careers" className="py-32 bg-white px-6">
    <div className="max-w-7xl mx-auto bg-neutral-50 border border-neutral-200 rounded-[3rem] p-12 md:p-24 text-center overflow-hidden relative group hover:bg-white hover:shadow-2xl hover:shadow-black/5 transition-all duration-500">
      <div className="absolute top-0 right-0 w-[50%] h-full bg-gradient-to-l from-black/[0.02] to-transparent pointer-events-none group-hover:from-black/[0.04] transition-all duration-700" />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl md:text-6xl text-black font-bold mb-6 tracking-tight">Join the Team</h2>
        <p className="text-neutral-600 text-lg mb-10 max-w-2xl mx-auto">
          We are always looking for curious minds, quantitative thinkers, and builders who live at the intersection of Finance & Tech.
        </p>
        <a 
          href="/careers" 
          className="inline-flex items-center gap-3 bg-black text-white px-10 py-5 rounded-full font-bold hover:bg-neutral-800 transition-all uppercase tracking-widest text-sm shadow-xl"
        >
          Explore Careers <ArrowRight size={18} />
        </a>
      </motion.div>
    </div>
  </section>
);

const ContactBrief = () => (
    <section id="contact" className="pb-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center bg-neutral-50 p-5 sm:p-12 md:p-20 rounded-[3rem] border border-neutral-100 group hover:shadow-2xl hover:shadow-black/5 transition-all duration-500">
                <div>
                    <h2 className="text-4xl font-bold mb-6 tracking-tight">Let's build the future together</h2>
                    <p className="text-neutral-500 mb-8 max-w-sm leading-relaxed text-lg">
                        Whether you have a specific idea or just a workflow problem, we're ready to engineer a solution.
                    </p>
                    <div className="space-y-4">
                        <div className="flex items-center gap-3 text-neutral-600 group-hover:text-black transition-colors">
                            <div className="w-9 h-9 md:w-10 md:h-10 bg-white rounded-xl flex items-center justify-center shadow-sm shrink-0">
                                <Mail className="w-4 h-4 md:w-[18px] md:h-[18px]" />
                            </div>
                            <span className="font-medium text-sm md:text-base break-all md:break-normal">arhamtechnologyindia@gmail.com</span>
                        </div>
                        <div className="flex items-center gap-3 text-neutral-600 group-hover:text-black transition-colors">
                            <div className="w-9 h-9 md:w-10 md:h-10 bg-white rounded-xl flex items-center justify-center shadow-sm shrink-0">
                                <Phone className="w-4 h-4 md:w-[18px] md:h-[18px]" />
                            </div>
                            <span className="font-medium text-sm md:text-base">+91 7228882088</span>
                        </div>
                    </div>
                </div>
                <div className="flex justify-end">
                    <a 
                      href="/contact" 
                      className="w-full md:w-auto text-center bg-black text-white px-8 md:px-12 py-5 md:py-6 rounded-full font-bold hover:bg-neutral-800 transition-all uppercase tracking-widest text-[13px] md:text-sm shadow-2xl hover:translate-y-[-4px]"
                    >
                        Send a Message
                    </a>
                </div>
            </div>
        </div>
    </section>
);

const testimonials = [
  { name: "Arham", role: "Prop Trader", text: "The IPO Utility transformed our workflow completely. We no longer worry about manual checks across registrars.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Arham" },
  { name: "Devam", role: "Market Participant", text: "Fast, reliable, and precise tools. Arham FinTech is setting new standards for high-performance trading software.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Devam" },
  { name: "Amee", role: "Tech Consultant", text: "Excellently designed and extremely intuitive. Their approach to solving complex market problems is unmatched.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Amee" },
  { name: "Tejas", role: "Investment Manager", text: "Simply the best custom algo development team in India. They deliver high-throughput solutions every time.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Tejas" },
  { name: "Chirag", role: "Operations Head", text: "The Corporate Actions app has missed zero details since we integrated it. A must-have for serious players.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Chirag" },
  { name: "Ronak", role: "Quantitative Analyst", text: "Robust and scalable. Their order flow logic and slippage protection are light years ahead of the rest.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Ronak" },
  { name: "Deepak", role: "Retail Broker", text: "Happy customer since 2018. Their support team and technical expertise have consistently exceeded expectations.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Deepak" },
  { name: "Hiren", role: "Active Trader", text: "The futuristic ideas they bring to the table are revolutionary. They truly understand the pulse of the market.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Hiren" },
  { name: "Meet", role: "System Architect", text: "Seamless integration and high-performance throughput. Their tools are built by traders, for traders.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Meet" },
  { name: "Param", role: "Risk Manager", text: "Clean code and high performance. Their attention to detail in handling market data is professional.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Param" },
  { name: "Darshan", role: "Trading Head", text: "The prediction accuracy of their OFS tool is game-changing for our institutional desk.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Darshan" },
  { name: "Ajay", role: "Compliance Officer", text: "Simplified our premium management and reporting overnight. Robust and user-friendly.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Ajay" },
  { name: "Utsav", role: "Portfolio Manager", text: "Very satisfied with the custom dashboards they built. Precise analytics with zero lag.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Utsav" },
  { name: "Devarsh", role: "Tech Lead", text: "Professional team that truly understands market nuances. Their delivery cycle is very impressive.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Devarsh" },
  { name: "Yash", role: "Hedge Fund Manager", text: "Responsive and high-throughput solutions. A reliable partner for any scale of fintech innovation.", image: "https://api.dicebear.com/7.x/avataaars/svg?seed=Yash" }
];

const TestimonialsColumn = (props) => {
  return (
    <div className={props.className}>
      <motion.div
        animate={{
          translateY: "-50%",
        }}
        transition={{
          duration: props.duration || 10,
          repeat: Infinity,
          ease: "linear",
          repeatType: "loop",
        }}
        className="flex flex-col gap-6 pb-6"
      >
        {[...new Array(2)].fill(0).map((_, index) => (
          <React.Fragment key={index}>
            {props.testimonials.map(({ text, image, name, role }, i) => (
              <div className="p-10 rounded-3xl border border-neutral-100 shadow-xl shadow-black/5 bg-white max-w-xs w-full" key={i}>
                <div className="text-neutral-600 leading-relaxed text-sm">"{text}"</div>
                <div className="flex items-center gap-3 mt-8">
                  <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center text-xs font-bold text-black border border-neutral-200 uppercase">
                    {name.charAt(0)}
                  </div>
                  <div className="flex flex-col">
                    <div className="font-bold text-black text-xs uppercase tracking-widest">{name}</div>
                    <div className="text-[10px] text-neutral-400 font-bold uppercase tracking-tight">{role}</div>
                  </div>
                </div>
              </div>
            ))}
          </React.Fragment>
        ))}
      </motion.div>
    </div>
  );
};

const Testimonials = () => {
  const firstColumn = testimonials.slice(0, 5);
  const secondColumn = testimonials.slice(5, 10);
  const thirdColumn = testimonials.slice(10, 15);

  return (
    <section className="bg-white py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 z-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          viewport={{ once: true }}
          className="flex flex-col items-center justify-center max-w-[640px] mx-auto mb-20"
        >
          <div className="flex justify-center mb-6">
            <div className="border border-neutral-200 py-1.5 px-5 rounded-full text-[10px] font-bold uppercase tracking-widest text-neutral-500">Testimonials</div>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-center text-black mb-8 leading-[1.1]">
            What our users say <br /> across the market
          </h2>
          <p className="text-center text-neutral-500 text-lg leading-relaxed max-w-lg">
            See why professional traders and institutions trust Arham FinTech to power their daily workflows.
          </p>
        </motion.div>

        <div className="flex justify-center gap-8 [mask-image:linear-gradient(to_bottom,transparent,black_15%,black_85%,transparent)] max-h-[800px] overflow-hidden">
          <TestimonialsColumn testimonials={firstColumn} duration={25} />
          <TestimonialsColumn testimonials={secondColumn} className="hidden md:block" duration={35} />
          <TestimonialsColumn testimonials={thirdColumn} className="hidden lg:block" duration={30} />
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
    return (
        <div className="overflow-x-hidden">
            <ScrollProgress />
            <Navbar />
            <Hero />
            <Marquee />
            <ServicesPreview />
            <ScrollingProducts />
            <CareersTeaser />
            <CompanyPreview />
            <Testimonials />
            <ContactBrief />
            <Footer />
        </div>
    );
};

const Footer = () => {
  const footerSections = [
    {
      title: 'Resources',
      links: [
        { name: 'NSE India', href: 'https://www.nseindia.com/' },
        { name: 'BSE Limited', href: 'https://www.bseindia.com/' },
        { name: 'MCX India', href: 'https://www.mcxindia.com/' },
        { name: 'Market News', href: 'https://www.moneycontrol.com/news/business/markets/' },
        { name: 'AMFI Data', href: 'https://www.amfiindia.com/' },
      ]
    },
    {
      title: 'Markets',
      links: [
        { name: 'Nifty 50 (NSE)', href: 'https://www.nseindia.com/market-data/live-equity-market' },
        { name: 'Sensex (BSE)', href: 'https://www.bseindia.com/sensex/IndexHighlight.html' },
        { name: 'Global Indices', href: 'https://www.investing.com/indices/major-indices' },
        { name: 'Trading Holidays (NSE)', href: 'https://www.nseindia.com/resources/exchange-communication-holidays' },
        { name: 'Banking Holidays (RBI)', href: 'https://www.rbi.org.in/Scripts/HolidayMatrixDisplay.aspx' },
      ]
    },
    {
      title: 'Navigate',
      links: [
        { name: 'Home', href: '/' },
        { name: 'Services', href: '/services' },
        { name: 'Products', href: '/products' },
        { name: 'Careers', href: '/careers' },
        { name: 'Company', href: '/company' },
        { name: 'Contact', href: '/contact' },
      ]
    },
    {
      title: 'Beyond FinTech',
      links: [
        { name: 'Dholera', href: '/dholera.html' },
      ]
    }
  ];

  return (
    <footer className="bg-white pt-16 pb-12 border-t border-neutral-200">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-12 gap-x-8 mb-20">
          {/* Brand Info */}
          <div className="col-span-2 space-y-6 lg:pr-12">
            <img src="/static/arham_logo.webp" alt="ARHAM FinTech" className="h-16 w-auto object-contain" />
            <p className="max-w-sm text-neutral-500 leading-relaxed text-sm">
              Pioneering the intersection of finance and technology. We build the tools that power tomorrow's markets.
            </p>
            <div className="space-y-4 pt-4">
              <h4 className="text-[10px] font-bold text-neutral-400 uppercase tracking-[0.2em]">Stay Connected</h4>
              <div className="flex gap-3">
                <a href="mailto:arhamtechnologyindia@gmail.com" className="w-9 h-9 md:w-10 md:h-10 bg-neutral-100 rounded-full flex items-center justify-center hover:bg-black hover:text-white transition-all duration-300" title="Email">
                  <Mail className="w-4 h-4 md:w-[18px] md:h-[18px]" />
                </a>
                <a href="https://wa.me/917228882088" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-neutral-100 rounded-full flex items-center justify-center hover:bg-black hover:text-white transition-all duration-300" title="WhatsApp">
                  <MessageCircle className="w-4 h-4 md:w-[18px] md:h-[18px]" />
                </a>
                <a href="https://in.linkedin.com/company/arhamfintech" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-neutral-100 rounded-full flex items-center justify-center hover:bg-black hover:text-white transition-all duration-300" title="LinkedIn">
                  <Linkedin className="w-4 h-4 md:w-[18px] md:h-[18px]" />
                </a>
                <a href="https://www.youtube.com/@arhamtechnology1673" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-neutral-100 rounded-full flex items-center justify-center hover:bg-black hover:text-white transition-all duration-300" title="YouTube">
                  <Youtube className="w-4 h-4 md:w-[18px] md:h-[18px]" />
                </a>
                <a href="https://www.instagram.com/arhamfintech?igsh=YjBoMzE2bXdmajR6" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-neutral-100 rounded-full flex items-center justify-center hover:bg-black hover:text-white transition-all duration-300" title="Instagram">
                  <Instagram className="w-4 h-4 md:w-[18px] md:h-[18px]" />
                </a>
                <a href="https://t.me/+917228882088" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-neutral-100 rounded-full flex items-center justify-center hover:bg-black hover:text-white transition-all duration-300" title="Telegram">
                  <Send className="w-4 h-4 md:w-[18px] md:h-[18px]" />
                </a>
              </div>
            </div>
          </div>

          {/* Links Sections */}
          {footerSections.map((section, idx) => (
             <div key={idx} className="space-y-6">
                <h4 className="font-bold text-black text-[11px] uppercase tracking-widest border-b border-neutral-100 pb-2">{section.title}</h4>
                <ul className="space-y-3 text-[11px] text-neutral-500 font-medium">
                   {section.links.map(link => (
                       <li key={link.name}>
                         <a href={link.href} target={link.href.startsWith('http') ? '_blank' : '_self'} rel={link.href.startsWith('http') ? 'noopener noreferrer' : ''} className="hover:text-black transition-colors block">
                           {link.name}
                         </a>
                       </li>
                   ))}
                </ul>
             </div>
          ))}
        </div>

        <div className="pt-8 border-t border-neutral-100 flex justify-center items-center text-[10px] text-neutral-400 font-medium uppercase tracking-wider">
          <p className="normal-case text-neutral-500 text-center">
            © {new Date().getFullYear()} ADWEALTH PRIVATE LIMITED. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

function ProductDetailRoute() {
  const { id } = useParams();
  return (
    <>
      <ScrollProgress />
      <Navbar />
      <ProductDetail productId={id} />
      <Footer />
    </>
  );
}

function ProductsRoute() {
  return (
    <>
      <ScrollProgress />
      <Navbar />
      <Products />
      <Footer />
    </>
  );
}

function ServicesRoute() {
  return (
    <>
      <ScrollProgress />
      <Navbar />
      <Services />
      <Footer />
    </>
  );
}

function CompanyRoute() {
  return (
    <>
      <ScrollProgress />
      <Navbar />
      <Company />
      <Footer />
    </>
  );
}

function CareersRoute() {
  return (
    <>
      <ScrollProgress />
      <Navbar />
      <Careers />
      <Footer />
    </>
  );
}

function ContactRoute() {
  return (
    <>
      <ScrollProgress />
      <Navbar />
      <Contact />
      <Footer />
    </>
  );
}

export default function ArhamFintechV2() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/product/:id" element={<ProductDetailRoute />} />
        <Route path="/products" element={<ProductsRoute />} />
        <Route path="/services" element={<ServicesRoute />} />
        <Route path="/company" element={<CompanyRoute />} />
        <Route path="/careers" element={<CareersRoute />} />
        <Route path="/contact" element={<ContactRoute />} />
        <Route path="/contact/*" element={<ContactRoute />} />
      </Routes>
      <BackToTop />
    </Router>
  );
}